import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { usePermissions } from '../../hooks/use-permissions';

interface RouteGuardProps {
  children: ReactNode;
  permission?: string;
  permissions?: string[];
  role?: string;
  roles?: string[];
  requireAll?: boolean; // Whether to require ALL permissions/roles (default: false - requires ANY)
  redirectTo?: string; // Where to redirect if access denied (default: '/')
}

// eslint-disable-next-line react/function-component-definition
const RouteGuard: React.FC<RouteGuardProps> = ({
  children,
  permission,
  permissions,
  role,
  roles,
  requireAll,
  redirectTo,
}) => {
  const { hasPermission, hasRole, hasAnyPermission, hasAnyRole, hasAllPermissions, hasAllRoles } = usePermissions();

  let hasAccess = true;

  // Check single permission
  if (permission && !hasPermission(permission)) {
    hasAccess = false;
  }

  // Check multiple permissions
  if (permissions && permissions.length > 0) {
    if (requireAll) {
      hasAccess = hasAccess && hasAllPermissions(permissions);
    } else {
      hasAccess = hasAccess && hasAnyPermission(permissions);
    }
  }

  // Check single role
  if (role && !hasRole(role)) {
    hasAccess = false;
  }

  // Check multiple roles
  if (roles && roles.length > 0) {
    if (requireAll) {
      hasAccess = hasAccess && hasAllRoles(roles);
    } else {
      hasAccess = hasAccess && hasAnyRole(roles);
    }
  }

  if (!hasAccess) {
    return <Navigate to={redirectTo || '/'} replace />;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

RouteGuard.defaultProps = {
  permission: undefined,
  permissions: undefined,
  role: undefined,
  roles: undefined,
  requireAll: false,
  redirectTo: '/',
};

export default RouteGuard;
